import useAppStore from "../stores/appStore";
import { Message, MessagePrev } from "../types";

const pickNonEmptyTitle = (obj: Record<string, unknown> | null | undefined): string => {
  if (!obj) return "";
  const keys = ["title", "lesson_title", "lessonTitle", "tabbed_title", "tabbedTitle"];
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

export const getLessonTitleFromAny = (msg: unknown): string => {
  const root = (msg as Record<string, unknown>) || {};
  const direct = pickNonEmptyTitle(root);
  if (direct) return direct;
  const tabs = root.tabbed_content;
  if (Array.isArray(tabs)) {
    for (const tab of tabs) {
      if (tab && typeof tab === "object") {
        const tabTitle = pickNonEmptyTitle(tab as Record<string, unknown>);
        if (tabTitle) return tabTitle;
      }
    }
  }
  return "";
};

interface ResolveLessonTitleOptions {
  lessonTitleCache?: Record<string, string>;
  sources?: MessagePrev[];
}

export const resolveLessonTitle = (
  msg: MessagePrev,
  options: ResolveLessonTitleOptions = {}
): string => {
  const direct = getLessonTitleFromAny(msg);
  if (direct) return direct;

  const { lessonTitleCache = {}, sources = [] } = options;
  const fromCache = lessonTitleCache[msg.message_id];
  if (fromCache) return fromCache;

  const sourceById = sources.find((m) => m.message_id === msg.message_id);
  const fromSourceById = getLessonTitleFromAny(sourceById);
  return fromSourceById;
};

interface ResolveTabbedDisplayTextOptions extends ResolveLessonTitleOptions {
  tabLabelsCache?: Record<string, string[]>;
}

const getFirstNonEmptyLine = (text?: string): string => {
  if (!text) return "";
  const lines = text.split(/\r?\n/).map((line) => line.trim());
  return lines.find((line) => line.length > 0) || "";
};

export const resolveTabbedDisplayText = (
  msg: MessagePrev,
  options: ResolveTabbedDisplayTextOptions = {},
): string => {
  const lessonTitle = resolveLessonTitle(msg, options);
  if (lessonTitle) return lessonTitle;

  const firstLine = getFirstNonEmptyLine(msg.text);
  if (firstLine) return firstLine;

  const labelsFromMessage =
    msg.tabbed_content
      ?.map((tab) => tab?.tab_label?.trim())
      .filter((label): label is string => Boolean(label && label.length > 0)) || [];
  if (labelsFromMessage.length > 0) return labelsFromMessage.join(" · ");

  const labelsFromCache = options.tabLabelsCache?.[msg.message_id] ?? [];
  if (labelsFromCache.length > 0) return labelsFromCache.join(" · ");

  return "";
};

const isAutoGeneratedAudioFilename = (name: string): boolean => {
  if (!name || name.length < 20) return false;
  const ext = name.split(".").pop()?.toLowerCase() || "";
  const audioExts = ["webm", "mp3", "ogg", "wav", "m4a"];
  if (!audioExts.includes(ext)) return false;
  const base = name.replace(/\.[^.]+$/, "");
  return /^[0-9a-f_-]+$/i.test(base);
};

export const getAudioDisplayText = (msg: MessagePrev): string => {
  if (msg.type !== "audio") return msg.text || "";
  const trimmedText = (msg.text || "").trim();
  if (trimmedText.toLowerCase() === "voice message") {
    return "Audio Voice Message";
  }
  if (trimmedText && !trimmedText.startsWith("http") && trimmedText.length < 200) {
    if (isAutoGeneratedAudioFilename(trimmedText)) return "Audio Voice Message";
    return trimmedText;
  }
  const url = msg.file_url || msg.text || "";
  const filename = decodeURIComponent(
    (url.split("/").pop() || "").split("?")[0] || ""
  );
  if (filename) {
    if (isAutoGeneratedAudioFilename(filename)) return "Audio Voice Message";
    return filename;
  }
  return "Audio Voice Message";
};

export const getVideoDisplayText = (_msg: MessagePrev): string => {
  return "Video Message";
};

export const isSuggestionTabLabel = (label?: string): boolean =>
  (label ?? "").toLowerCase().trim().includes("suggestion");

export const createPlaceholderMessage = (): Message => {
  const { focus, domain } = useAppStore.getState();
  const tempId = `temp-${Date.now()}`;
  return {
    message_id: tempId,
    text: "",
    is_bot: true,
    isPlaceholder: true,
    minHeightFull: false,
    type: "placeholder",
    user_id: "bot",
    date: new Date().toISOString(),
    reaction: "",
    isSaved: false,
    message_type: "default",
    focus,
    domain,
  };
};

export const createDesktopRequiredSystemMessage = (): Message => {
  const { focus, domain } = useAppStore.getState();
  const messageId = `system-${Date.now()}`;
  return {
    message_id: messageId,
    text: `⚠️ Roleplay may not work well on mobile devices.`,
    is_bot: true,
    type: "button",
    user_id: "bot",
    date: new Date().toISOString(),
    reaction: "",
    isSaved: false,
    message_type: "system",
    focus,
    domain,
    buttons: [
      [
        { text: "Continue", data: "continue_mobile_warning" },
        { text: "Exit", data: "exit_mobile_warning" },
      ],
    ],
  };
};

// Text helpers for TTS caching/versioning
export const normalizeText = (s: string | undefined | null): string => {
  return (s || "").replace(/\s+/g, " ").trim();
};

export const hash32 = (s: string): string => {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16);
};

export const buildUrlWithParams = (
  url: string,
  params: Record<string, string>
): string => {
  try {
    const u = new URL(url, window.location.origin);
    Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, v));
    return u.toString();
  } catch {
    const sep = url.includes("?") ? "&" : "?";
    const q = Object.entries(params)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&");
    return `${url}${sep}${q}`;
  }
};

// Minimal IndexedDB helpers for caching audio blobs by a stable key
const DB_NAME = "tts-audio-cache";
const STORE_NAME = "audio";
let dbPromise: Promise<IDBDatabase> | null = null;

const openDb = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
};

export const idbGetAudioBlob = async (key: string): Promise<Blob | null> => {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);
      req.onsuccess = () => resolve((req.result as Blob) || null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
};

export const idbPutAudioBlob = async (
  key: string,
  blob: Blob
): Promise<void> => {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(blob, key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // ignore
  }
};

export const idbDeleteAudioBlob = async (key: string): Promise<void> => {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // ignore
  }
};
